import { ChevronRight, IndianRupee, Scale, Truck, MapPin, Star, Clock, TrendingUp } from "lucide-react";
import { fmtDate } from "@/lib/api";
import { usePT } from "@/lib/pt";
import { PartnerAlerts } from "@/components/PartnerAlerts";
import { Eligibility } from "@/components/PartnerEligibility";
import { OnlineToggle } from "@/components/PartnerLive";
import { VerifiedTick } from "@/components/VerifiedTick";
import { TargetRing } from "@/components/TargetRing";

const greet = () => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening"; };

const Hello = ({ data }) => {
  const { pt } = usePT();
  const h = data.service_hours || {};
  return (
    <div className="relative overflow-hidden rounded-[2rem] bg-[#0D3B2B] p-5 text-white" data-testid="partner-hello">
      <img src="/partner-welcome.webp" alt="" className="pointer-events-none absolute -right-10 top-0 h-full w-2/3 object-cover opacity-30" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#0D3B2B] via-[#0D3B2B]/80 to-transparent" />
      <div className="relative space-y-2">
        <p className="text-sm font-semibold text-[#9BE3BF]">{pt(greet())} · {fmtDate(new Date().toISOString().slice(0, 10))}</p>
        <h1 className="font-display text-3xl font-extrabold" data-testid="partner-name-heading">{pt("Hi")} {data.name.split(" ")[0]}</h1>
        <div className="flex flex-wrap items-center gap-2 text-xs font-bold">
          {data.star && <span className="flex items-center gap-1 rounded-full bg-[#F5B83D] px-2 py-1 text-[#0D3B2B]" data-testid="partner-home-star"><Star className="h-4 w-4 fill-current" />{pt("Star Partner")}</span>}
          {data.rating?.avg && <span className="flex items-center gap-1 rounded-full bg-white/15 px-2 py-1" data-testid="partner-home-rating"><Star className="h-4 w-4 fill-[#F5B83D] text-[#F5B83D]" />{data.rating.avg} ({data.rating.count})</span>}
          <span className={`flex items-center gap-1 rounded-full px-2 py-1 ${h.now_open ? "bg-[#2E9E6B]" : "bg-white/15"}`} data-testid="partner-home-hours">
            <Clock className="h-4 w-4" />{pt(h.now_open ? "Open now" : "Closed now")} · 8–8</span>
          <VerifiedTick ok={data.mobile_verified} label={pt("Mobile verified")} testid="partner-home-verified" />
        </div>
      </div>
    </div>
  );
};

const Next = ({ next, count, go }) => {
  const { pt } = usePT();
  return (
    <button onClick={() => go("pickups")} className="w-full rounded-3xl border-2 border-[#0D3B2B] bg-white p-5 text-left transition-transform active:scale-[.98]" data-testid="partner-next-stop">
      <div className="flex items-center justify-between">
        <p className={`rounded-full px-3 py-1 text-sm font-bold ${next.overdue ? "bg-[#FDECE7] text-[#B4401F]" : "bg-[#E7F6EE] text-[#1F7A50]"}`}>{pt(next.overdue ? "Late · go now" : "Next pickup")}</p>
        <p className="text-sm font-bold text-[#4A6358]" data-testid="partner-next-count">{count} {pt("today")}</p>
      </div>
      <p className="mt-2 font-display text-2xl font-extrabold text-[#0D3B2B]">{next.customer || next.booking_id}</p>
      <p className="flex items-center gap-1 text-base text-[#4A6358]"><Clock className="h-5 w-5" />{fmtDate(next.date)} · {next.slot_label}</p>
      {(next.area || next.address) && <p className="flex items-center gap-1 truncate text-base text-[#4A6358]"><MapPin className="h-5 w-5 shrink-0" />{next.area || next.address}</p>}
      {!!next.scrap_types?.length && <div className="mt-2 flex flex-wrap gap-1">{next.scrap_types.slice(0, 4).map((s) => <span key={s} className="rounded-full bg-[#F4F8F5] px-2 py-0.5 text-xs font-semibold text-[#0D3B2B]">{s}</span>)}</div>}
      <p className="mt-3 flex h-14 items-center justify-center gap-1 rounded-2xl bg-[#E05C3A] text-lg font-bold text-white">{pt("Open")}<ChevronRight className="h-6 w-6" /></p>
    </button>
  );
};

const Tile = ({ Icon, v, l, k, tone = "bg-white" }) => (
  <div className={`rounded-2xl p-3 shadow-sm ${tone}`}><Icon className="h-6 w-6 text-[#2E9E6B]" />
    <p className="mt-1 font-display text-xl font-extrabold text-[#0D3B2B]" data-testid={`partner-today-${k}`}>{v}</p><p className="text-sm text-[#4A6358]">{l}</p></div>
);

const Money = ({ e }) => {
  const { pt } = usePT();
  const t = e.today, w = e.week;
  return (
    <div className="space-y-2" data-testid="partner-home-money">
      <div className="flex items-end justify-between rounded-3xl bg-[#E7F6EE] p-4">
        <div><p className="text-sm font-bold text-[#1F7A50]">{pt("Paid to customers today")}</p>
          <p className="font-display text-4xl font-extrabold text-[#0D3B2B]" data-testid="partner-today-payout">₹{t.payout}</p></div>
        <IndianRupee className="h-10 w-10 text-[#2E9E6B]" />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Tile Icon={Truck} v={t.pickups} l={pt("Today")} k="pickups" />
        <Tile Icon={Scale} v={`${t.kg} kg`} l={pt("Collected")} k="kg" />
        <Tile Icon={TrendingUp} v={w.pickups} l={pt("This week")} k="week" />
      </div>
    </div>
  );
};

export const HomeTab = ({ data, pos, token, online, onOnline, go }) => {
  const { pt } = usePT();
  const next = data.jobs[0];
  const near = (data.nearby || []).reduce((a, n) => a + (n.count || 1), 0);
  return (
    <div className="space-y-4" data-testid="partner-home-tab">
      <Hello data={data} />
      {pos === false && <p className="rounded-2xl bg-[#FEF3C7] p-3 text-base font-semibold text-[#92400E]" data-testid="partner-location-off">{pt("Turn on location")}</p>}
      <OnlineToggle token={token} online={online} onChange={onOnline} />
      <PartnerAlerts token={token} vapid={data.vapid_public_key} compact />
      {data.eligibility && !data.eligibility.ready && <Eligibility e={data.eligibility} onlyBlockers />}
      {next ? <Next next={next} count={data.jobs.length} go={go} />
        : <div className="rounded-3xl bg-white p-6 text-center" data-testid="partner-no-jobs">
            <Truck className="mx-auto h-10 w-10 text-[#BFE3CF]" />
            <p className="mt-2 text-base font-semibold text-[#4A6358]">{pt(online ? "No pickups now. Stay online." : "Go online to get pickups.")}</p></div>}
      {online && near > 0 && (
        <button onClick={() => go("map")} className="flex w-full items-center gap-3 rounded-3xl bg-[#FFF4E0] p-4 text-left" data-testid="partner-home-nearby">
          <MapPin className="h-8 w-8 shrink-0 text-[#E05C3A]" />
          <span className="flex-1 text-base font-bold text-[#0D3B2B]">{near} {pt("pickups waiting near you")}</span><ChevronRight className="h-6 w-6 text-[#E05C3A]" /></button>
      )}
      <TargetRing t={data.target} />
      <Money e={data.earnings} />
    </div>
  );
};
